import React, { useState } from 'react';
import { useEditOrderModalQuery } from '../../graphql';
import { Modal } from '../ui';
import type { OnSaveType } from './OrderForm';
import OrderForm from './OrderForm';
import type { ModalProps } from '../ui/Modal/Modal';
import { ModalTabItem, ModalTabs } from '../ui/Modal/Modal';
import { useUpdateOrderWithRelationsMutation } from './order';
import { assertDefined, hasId } from '../../lib/util';
import { toast } from 'react-toastify';
import { useApolloClient } from '@apollo/client';
import { LoadingOverlay } from '../LoadingSpinner';
import OrderChangelog from './OrderChangeLog';
import { useCurrentUser } from '../../state/zustand';
import { softDeleteOrders } from './OrderTable';

export type EditOrderModalProps = {
  disableDelete?: boolean;
  onPrevious: () => void;
  onClose: () => void;
  onDelete?: () => void;
  isOrderAssigned?: (orderId: number) => boolean;
  isOrderPublished?: (orderId: number) => boolean;
  orderId?: number;
};

const EditOrderModal = ({
  onPrevious,
  onClose,
  orderId,
  onDelete,
  isOrderAssigned,
  isOrderPublished,
  disableDelete = false,
  ...restProps
}: EditOrderModalProps & Partial<ModalProps>): null | JSX.Element => {
  assertDefined(orderId, 'EditOrderModal order id');
  const currentUser = useCurrentUser();
  assertDefined(currentUser, 'Current user not available');

  const {
    data: orderData,
    loading: orderLoading,
    refetch,
  } = useEditOrderModalQuery({
    variables: {
      orderId,
    },
  });

  const [activeTab, setActiveTab] = useState<
    'order' | 'contract' | 'changelogs'
  >('order');

  const [updateOrderWithRelationsMutation, { loading: updateLoading }] =
    useUpdateOrderWithRelationsMutation();

  const apolloClient = useApolloClient();

  const [softDeleteLoading, setSoftDeleteLoading] = useState(false);

  const order = orderData?.orderById ?? null;
  const readOnly =
    !order ||
    !currentUser?.userRights?.nodes.some((userRight) => {
      return (
        userRight?.createAndEditOrders &&
        userRight?.organizationByOrganizationId?.descendants?.includes(
          order.organizationId,
        )
      );
    });

  const onSave: OnSaveType = async (
    {
      order,
      orderDetail,
      cargoExclusions,
      previousExclusions,
      specialRequirements,
      previousSpecialRequirements,
      orderAttributes,
      previousOrderAttributes,
    },
    opts,
  ) => {
    if (!hasId<typeof order, number>(order)) {
      // This shouldn't really happen unless there's a bug
      throw new Error(`Required id missing on order`);
    }

    if (!previousExclusions) {
      // This shouldn't really happen unless there's a bug
      throw new Error(`Previous exclusions not passed`);
    }

    try {
      await updateOrderWithRelationsMutation(
        order,
        orderDetail,
        cargoExclusions,
        previousExclusions,
        specialRequirements,
        previousSpecialRequirements,
        orderAttributes,
        previousOrderAttributes,
      );

      toast.success(`Order updated.`);
      refetch().then(() => {
        if (opts?.shouldClose) {
          onPrevious();
        }
      });
    } catch (err) {
      console.error('Error saving edited order', err.message);
      toast.error(
        `Could not save order or its associated details: ${err.message}`,
      );
      return [err];
    }
    return [];
  };

  const handleDelete = async () => {
    try {
      setSoftDeleteLoading(true);
      await softDeleteOrders(apolloClient, [orderId]);
      toast.success(`Order archived`);
      onDelete?.();
      onClose();
    } catch (err) {
      toast.error(`Unable to delete order: ${err.message}`);
    } finally {
      setSoftDeleteLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} onPrevious={onPrevious} {...restProps}>
      {orderLoading ? (
        <LoadingOverlay message="Loading order" />
      ) : (
        <>
          <ModalTabs>
            <ModalTabItem
              label="Order"
              isActive={activeTab === 'order'}
              onPress={() => setActiveTab('order')}
            >
              Order
            </ModalTabItem>
            <ModalTabItem
              label="Changelogs"
              isActive={activeTab === 'changelogs'}
              onPress={() => setActiveTab('changelogs')}
            >
              Changelogs
            </ModalTabItem>
          </ModalTabs>
          {activeTab === 'order' && order && (
            <OrderForm
              readOnly={readOnly}
              order={order}
              onSave={onSave}
              onCancel={onPrevious}
              onDelete={!disableDelete ? handleDelete : undefined}
              isLoading={updateLoading || softDeleteLoading}
              isAssigned={isOrderAssigned?.(orderId) ?? false}
              isPublished={isOrderPublished?.(orderId) ?? false}
            />
          )}
          {activeTab === 'changelogs' && order && (
            <OrderChangelog currentOrder={order}></OrderChangelog>
          )}
        </>
      )}
    </Modal>
  );
};

export default EditOrderModal;
